import { useCallback, useEffect, useState } from "react";

const usePagination = (
  initialData,
  baseUrl = "",
  fetchData,
  storageKey = "pagination"
) => {
  const getStorageKey = useCallback(() => {
    if (!storageKey) return null;

    return `taskwize_${storageKey}`;
  }, [storageKey]);

  const getSavedPage = useCallback(() => {
    const key = getStorageKey();

    if (!key) return 1;

    const savedPage = sessionStorage.getItem(key);

    if (!savedPage) return 1;

    const parsedPage = parseInt(savedPage, 10);

    return Number.isInteger(parsedPage) && parsedPage >= 1 ? parsedPage : 1;
  }, [getStorageKey]);

  const [currentData, setCurrentData] = useState([]);

  const [recordsData, setRecordsData] = useState({
    currentPage: getSavedPage(),
    totalPages: 1,
    total: 0,
  });

  const saveCurrentPage = useCallback(
    (page) => {
      const key = getStorageKey();

      if (!key) return;

      sessionStorage.setItem(key, String(page));
    },
    [getStorageKey]
  );

  const listPage = useCallback(
    async (page) => {
      if (!baseUrl || !fetchData) return;

      try {
        const separator = baseUrl.includes("?") ? "&" : "?";
        const url = `${baseUrl}${separator}page=${page}`;

        const result = await fetchData(url, "GET");

        if (result == null) return;

        const pagination = result.pagination || {
          page: result.current_page || page,
          pages: result.total_pages || 1,
          total: result.total || 0,
        };

        const currentPage = pagination.page || page;

        setCurrentData(result.results ?? []);

        setRecordsData({
          currentPage,
          totalPages: pagination.pages || 1,
          total: pagination.total || 0,
        });

        saveCurrentPage(currentPage);
      } catch (err) {
        console.error("Pagination fetch error:", err);
      }
    },
    [baseUrl, fetchData, saveCurrentPage]
  );

  useEffect(() => {
    if (!initialData) {
      setCurrentData([]);

      setRecordsData({
        currentPage: getSavedPage(),
        totalPages: 1,
        total: 0,
      });

      return;
    }

    const pagination = initialData.pagination;

    const savedPage = getSavedPage();

    const currentPage =
      pagination?.page || initialData.current_page || savedPage;

    const totalPages = pagination?.pages || initialData.total_pages || 1;

    const total = pagination?.total || initialData.total || 0;

    setCurrentData(initialData.results ?? []);

    setRecordsData({
      currentPage,
      totalPages,
      total,
    });
  }, [initialData, getSavedPage]);

  return {
    currentData,
    recordsData,
    listPage,
    getSavedPage,
  };
};

export default usePagination;
